import React, { useState, useEffect, useRef, useReducer } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Lightbox } from 'rhinostyle';
import { API_LONG_REQUEST_TIMEOUT_MS } from '../constants/AppConstants';
import { isImageType, isImageMimeType } from '../helpers/ImageHelpers';
import { AppConstants } from '../constants';
import { getFilteredChatThreadEventIds } from '../selectors/chatSelectors';
import { handleDownloadClick, DOWNLOAD_WARNING } from '../helpers/PdfViewerHelpers';
import { shapeEventAttachments } from '../helpers/EventHelpers';
import { isAndroid } from '../helpers/BrowserHelpers';

function getImages(events, eventIds) {
  const eventsWithImages = eventIds.filter((id) => {
    const shapedAttachments = shapeEventAttachments(events[id].attachments || []);
    return shapedAttachments.length && shapedAttachments.some((shapeAttachment) => (isImageType(shapeAttachment.typeId) ||
    isImageType(shapeAttachment.attachmentTypeId) ||
    isImageMimeType(shapeAttachment.type)));
  });
  let images = [];
  eventsWithImages.forEach((item) => {
    images = images.concat(shapeEventAttachments(events[item].attachments));
  });
  return images;
}

function getDefaultImageIndex(images, lightboxAttachmentUrl) {
  for (let i = 0; i < images.length; i++) {
    if (images[i].attachmentUrl === lightboxAttachmentUrl) return i;
  }
  return 0;
}

const LightboxContainer = (props) => {
  const { events, eventIds, lightboxAttachmentUrl, toggleLightbox, isNativeApp } = props;
  const images = getImages(events, eventIds);
  const [currentImageIndex, setCurrentImageIndex] = useState(getDefaultImageIndex(images, lightboxAttachmentUrl));

  const baseUrl = AppConstants.FILE_BASE_URL;
  const n = images.length;

  // Session-scoped cache: url → blobUrl (null = fetch in progress)
  // Persists for the lifetime of the open lightbox, all blobs revoked on close.
  const blobCacheRef = useRef(new Map());
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  // Fetch url into cache if not already fetched or in-progress
  const prefetch = (url) => {
    if (!url || blobCacheRef.current.has(url)) return;
    blobCacheRef.current.set(url, null); // sentinel: in-progress, prevents duplicate requests
    axios.get(url, { responseType: 'blob', timeout: API_LONG_REQUEST_TIMEOUT_MS })
      .then(({ data }) => {
        blobCacheRef.current.set(url, URL.createObjectURL(data));
        forceUpdate();
      })
      .catch(() => blobCacheRef.current.delete(url)); // allow retry on error
  };

  // Fetch current image immediately; prefetch adjacent for smooth navigation.
  // Cache prevents re-fetching images already loaded or in-flight.
  useEffect(() => {
    prefetch(`${baseUrl}${images[currentImageIndex].attachmentUrl}`);
    if (n > 1) {
      prefetch(`${baseUrl}${images[(currentImageIndex + 1) % n].attachmentUrl}`);
      prefetch(`${baseUrl}${images[(currentImageIndex + n - 1) % n].attachmentUrl}`);
    }
  }, [currentImageIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Revoke all blob URLs when the lightbox closes
  useEffect(() => () => {
    blobCacheRef.current.forEach((blobUrl) => { if (blobUrl) URL.revokeObjectURL(blobUrl); });
    blobCacheRef.current.clear();
  }, []);

  const mainSrc = blobCacheRef.current.get(`${baseUrl}${images[currentImageIndex].attachmentUrl}`) || null;
  const nextSrc = n > 1 ? (blobCacheRef.current.get(`${baseUrl}${images[(currentImageIndex + 1) % n].attachmentUrl}`) || null) : null;
  const prevSrc = n > 1 ? (blobCacheRef.current.get(`${baseUrl}${images[(currentImageIndex + n - 1) % n].attachmentUrl}`) || null) : null;

  const downloadUrl = `${baseUrl}${images[currentImageIndex].attachmentUrl}`;

  const isNativeAndroid = isNativeApp && isAndroid();

  const handleDownload = (e) => {
    // Native: confirm, then let the REAL <a> click navigate to the route URL (which has the
    // file extension) so the rhinonative shell downloads it. A blob: URL or a programmatic
    // link.click() never navigates as a user gesture, so the shell never fires — which is
    // why the old blob download silently did nothing on iOS.
    if (isNativeApp) {
      if (!window.confirm(DOWNLOAD_WARNING)) e.preventDefault(); // eslint-disable-line no-alert
      return;
    }
    // Web: keep the existing behavior — confirm + save the already-fetched same-origin blob.
    e.preventDefault();
    handleDownloadClick(mainSrc || downloadUrl, images[currentImageIndex].name, isNativeApp);
  };

  const lightboxProps = {
    mainSrc,
    nextSrc: n > 1 ? nextSrc : null,
    prevSrc: n > 1 ? prevSrc : null,
    onMovePrevRequest: () => setCurrentImageIndex((currentImageIndex + n - 1) % n),
    onMoveNextRequest: () => setCurrentImageIndex((currentImageIndex + 1) % n),
    onCloseRequest: () => toggleLightbox(false),
    animationDisabled: true,
    toolbarButtons: [
      <a //eslint-disable-line
        key="download"
        className="lightbox__download"
        href={downloadUrl}
        download={images[currentImageIndex].name}
        {...isNativeApp && !isNativeAndroid && { target: '_blank', rel: 'noopener noreferrer' }}
        onClick={handleDownload}
      />,
    ],
  };

  return <Lightbox {...lightboxProps} />;
};

LightboxContainer.propTypes = {
  eventIds: PropTypes.array.isRequired,
  events: PropTypes.object.isRequired,
  lightboxAttachmentUrl: PropTypes.string,
  toggleLightbox: PropTypes.func.isRequired,
  isNativeApp: PropTypes.bool,
};

function mapStateToProps(state, props) {
  if (props.threadType) {
    return {
      events: props.threadType === 'inbox' ? props.events : state[props.threadType].events,
      eventIds: props.threadType === 'inbox' ? props.eventIds : getFilteredChatThreadEventIds(state),
      lightboxAttachmentUrl: state.thread.lightboxAttachmentUrl,
      isNativeApp: state.nativeApp.isNativeApp,
    };
  }
  return {
    events: props.events,
    eventIds: props.eventIds,
    lightboxAttachmentUrl: props.lightboxAttachmentUrl,
    isNativeApp: state.nativeApp.isNativeApp,
  };
}
export default connect(mapStateToProps)(LightboxContainer);
